import {FC, useEffect, useMemo, useState} from 'react'
import axios from 'axios'
import moment from 'moment'
import {KTSVG} from '../../../../_metronic/helpers'
import {
  SeriesFutureAppointment,
  normalizeSeriesAppointment,
} from '../cancel-series-selection-modal/CancelSeriesSelectionModal'

const API_URL = process.env.REACT_APP_API_URL

export type RecurringSeriesSelectionMode = 'assign' | 'unassign'

type RecurringSeriesSelectionModalProps = {
  mode: RecurringSeriesSelectionMode
  appointmentId: number
  interpreterId?: string
  onClose: () => void
  onConfirm: (selectedAppointmentIds: number[]) => void
  isSubmitting?: boolean
}

const copyByMode = {
  assign: {
    title: 'Assign to Series Appointments',
    description:
      'Select the future open appointments in this series you want to assign this interpreter to.',
    empty: 'No future open unassigned appointments are available in this series.',
    confirm: 'Assign selected appointments',
    loading: 'Loading assignable appointments...',
  },
  unassign: {
    title: 'Unassign from Series Appointments',
    description:
      'Select the future appointments in this series you want to unassign this interpreter from.',
    empty: 'No future assigned appointments are available in this series for this interpreter.',
    confirm: 'Unassign selected appointments',
    loading: 'Loading assigned appointments...',
  },
}

const RecurringSeriesSelectionModal: FC<RecurringSeriesSelectionModalProps> = ({
  mode,
  appointmentId,
  interpreterId,
  onClose,
  onConfirm,
  isSubmitting = false,
}) => {
  const [appointments, setAppointments] = useState<SeriesFutureAppointment[]>([])
  const [selectedIds, setSelectedIds] = useState<number[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const labels = copyByMode[mode]

  useEffect(() => {
    document.body.classList.add('modal-open')
    return () => {
      document.body.classList.remove('modal-open')
    }
  }, [])

  useEffect(() => {
    const fetchAppointments = async () => {
      setIsLoading(true)
      setError(null)
      try {
        const url =
          mode === 'assign'
            ? `${API_URL}/Appoinment/series/${appointmentId}/future-assignable`
            : `${API_URL}/Appoinment/series/${appointmentId}/future-unassignable?interpreterId=${encodeURIComponent(
                interpreterId ?? ''
              )}`

        const {data} = await axios.get(url)
        const rawAppointments =
          data.result?.appointments ??
          data.result?.Appointments ??
          data.Result?.appointments ??
          data.Result?.Appointments

        if (data.status === 'S' && Array.isArray(rawAppointments)) {
          setAppointments(rawAppointments.map(normalizeSeriesAppointment))
        } else {
          setError(data.text || 'Failed to load appointments')
        }
      } catch (err: any) {
        setError(err?.response?.data?.text || 'Failed to load appointments')
      } finally {
        setIsLoading(false)
      }
    }

    fetchAppointments()
  }, [appointmentId, interpreterId, mode])

  const allSelected = useMemo(
    () => appointments.length > 0 && selectedIds.length === appointments.length,
    [appointments.length, selectedIds.length]
  )

  const toggleSelectAll = () => {
    if (allSelected) {
      setSelectedIds([])
    } else {
      setSelectedIds(appointments.map((a) => a.code))
    }
  }

  const toggleSelect = (code: number) => {
    setSelectedIds((prev) =>
      prev.includes(code) ? prev.filter((id) => id !== code) : [...prev, code]
    )
  }

  const formatDateTime = (startTime: string) => moment(startTime).format('MMM DD, YYYY • h:mm A')

  const handleConfirm = () => {
    if (selectedIds.length === 0) return
    onConfirm(selectedIds)
  }

  return (
    <>
      <div
        className='modal fade show d-block'
        id='kt_modal_recurring_series_selection'
        role='dialog'
        tabIndex={-1}
        aria-modal='true'
      >
        <div className='modal-dialog modal-dialog-centered mw-900px'>
          <div className='modal-content'>
            <div className='modal-header'>
              <h2 className='fw-bolder'>{labels.title}</h2>
              <div
                className='btn btn-icon btn-sm btn-active-icon-primary'
                style={{cursor: isSubmitting ? 'not-allowed' : 'pointer'}}
                onClick={() => {
                  if (!isSubmitting) onClose()
                }}
              >
                <KTSVG path='/media/icons/duotune/arrows/arr061.svg' className='svg-icon-1' />
              </div>
            </div>

            <div className='modal-body'>
              <p className='text-muted mb-5'>{labels.description}</p>

              {isLoading ? (
                <div className='text-center py-10'>
                  <span className='spinner-border spinner-border-sm align-middle me-2'></span>
                  {labels.loading}
                </div>
              ) : error ? (
                <div className='alert alert-danger'>{error}</div>
              ) : appointments.length === 0 ? (
                <div className='text-center py-10 text-muted'>{labels.empty}</div>
              ) : (
                <div className='table-responsive border rounded'>
                  <table className='table table-row-dashed table-row-gray-300 align-middle gs-0 gy-3 mb-0'>
                    <thead>
                      <tr className='fw-bold text-muted border-bottom'>
                        <th className='w-40px ps-4'>
                          <input
                            type='checkbox'
                            className='form-check-input'
                            checked={allSelected}
                            onChange={toggleSelectAll}
                            aria-label='Select all appointments'
                          />
                        </th>
                        <th>Appointment ID</th>
                        <th>Date &amp; Time</th>
                        <th>Language</th>
                        <th>Communication Type</th>
                      </tr>
                    </thead>
                    <tbody>
                      {appointments.map((appointment) => (
                        <tr key={appointment.code}>
                          <td className='ps-4'>
                            <input
                              type='checkbox'
                              className='form-check-input'
                              checked={selectedIds.includes(appointment.code)}
                              onChange={() => toggleSelect(appointment.code)}
                              aria-label={`Select appointment ${appointment.code}`}
                            />
                          </td>
                          <td className='fw-semibold'>{appointment.code}</td>
                          <td>{formatDateTime(appointment.startTime)}</td>
                          <td>{appointment.language || '-'}</td>
                          <td>{appointment.communicationType || '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {!isLoading && appointments.length > 0 && (
                <div className='mt-4 text-muted fw-semibold'>
                  {selectedIds.length} of {appointments.length} appointments selected
                </div>
              )}

              <div className='d-flex justify-content-end gap-3 mt-6'>
                <button className='btn btn-light' onClick={onClose} disabled={isSubmitting}>
                  Close
                </button>
                <button
                  type='button'
                  className='btn btn-success'
                  disabled={selectedIds.length === 0 || isLoading || isSubmitting}
                  onClick={handleConfirm}
                >
                  {isSubmitting ? (
                    <>
                      <span className='spinner-border spinner-border-sm align-middle me-2'></span>
                      {mode === 'assign' ? 'Assigning...' : 'Unassigning...'}
                    </>
                  ) : (
                    labels.confirm
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className='modal-backdrop fade show'></div>
    </>
  )
}

export {RecurringSeriesSelectionModal}
